import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Col, Container, Spinner} from 'react-bootstrap';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {FaCalendarCheck, FaClock, FaEuroSign, FaMapMarkerAlt, FaUserCheck, FaWallet} from 'react-icons/fa';
import * as api from '../api';
import {clearBookingInvitationResume, saveBookingInvitationResume} from '../util/booking-invitation-resume';

const BookingTopupInvitation = ({profile, authToken, match, history}) => {
  const {t, i18n} = useTranslation();
  const locale = i18n.language || 'it';
  const money = value => Number(value || 0).toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  const invitationToken = match.params.token;
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const loadInvitation = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    const response = await api.getBookingTopupInvitation(invitationToken, authToken);
    if (response.status === 200) {
      setInvitation(response.json);
      setError(null);
    } else if (response.json.error === 'topup_invitation_expired') {
      await loadInvitation();
    } else {
      setError(response.json.error || 'invitation_load_failed');
    }
    setLoading(false);
  }, [authToken, invitationToken]);

  useEffect(() => {
    if (!profile || !authToken) {
      saveBookingInvitationResume(invitationToken);
      history.replace(`/login?next=${encodeURIComponent(`/booking-invitation/${invitationToken}`)}`);
      return;
    }
    loadInvitation();
  }, [authToken, history, invitationToken, loadInvitation, profile]);

  const accept = async () => {
    setProcessing(true);
    const response = await api.acceptBookingTopupInvitation(invitationToken, authToken);
    if (response.status === 200) {
      clearBookingInvitationResume();
      setInvitation(response.json);
      setError(null);
    } else if (response.json.error === 'topup_invitation_not_enough_credit') {
      setInvitation(current => ({...current, shortfall: response.json.shortfall, participantAmount: response.json.requiredAmount}));
      setError(response.json.error);
    } else if (response.json.error === 'topup_invitation_expired') {
      await loadInvitation();
    } else {
      setError(response.json.error || 'invitation_accept_failed');
    }
    setProcessing(false);
  };

  const reject = async () => {
    setProcessing(true);
    const response = await api.rejectBookingTopupInvitation(invitationToken, authToken);
    if (response.status === 200) {
      clearBookingInvitationResume();
      setInvitation(response.json);
      setError(null);
    } else {
      setError(response.json.error || 'invitation_reject_failed');
    }
    setProcessing(false);
  };

  const recharge = () => {
    saveBookingInvitationResume(invitationToken);
    const amount = Math.max(5, Math.ceil(Number(invitation.shortfall || 0)));
    history.push(`/user-top-up-account?amount=${encodeURIComponent(amount)}&bookingInvitation=${encodeURIComponent(invitationToken)}`);
  };

  if (!profile || !authToken) return null;

  return (
    <main className="booking-invitation-page">
      <Container><Col xs={12} md={9} lg={7} className="mx-auto px-0">
        <section className="booking-invitation-hero">
          <span>{t('BOOKING_TOPUP_INVITE_EYEBROW')}</span>
          <h1><FaUserCheck aria-hidden="true"/> {t('BOOKING_TOPUP_INVITE_TITLE')}</h1>
          <p>{t('BOOKING_TOPUP_INVITE_INTRO')}</p>
        </section>
        {loading ? <div className="booking-invitation-card text-center"><Spinner animation="border"/><p>{t('BOOKING_TOPUP_LOADING')}</p></div> : null}
        {!loading && error && !invitation ? <div className="booking-invitation-card"><h2>{t('BOOKING_TOPUP_UNAVAILABLE_TITLE')}</h2><p>{t('BOOKING_TOPUP_UNAVAILABLE_BODY')}</p></div> : null}
        {!loading && invitation ? <section className="booking-invitation-card">
          <div className="booking-invitation-summary">
            <div><FaMapMarkerAlt/><span><small>{t('BOOKING_TOPUP_FIELD_SPORT')}</small><strong>{invitation.fieldName} · {invitation.sport}</strong></span></div>
            <div><FaCalendarCheck/><span><small>{t('BOOKING_TOPUP_DATE_TIME')}</small><strong>{new Date(invitation.startDatetime).toLocaleString(locale, {dateStyle: 'long', timeStyle: 'short'})}</strong></span></div>
            <div><FaEuroSign/><span><small>{t('BOOKING_TOPUP_CURRENT_SHARE')}</small><strong>€ {money(invitation.participantAmount)}</strong></span></div>
            <div><FaClock/><span><small>{t('BOOKING_TOPUP_DEADLINE')}</small><strong>{new Date(invitation.expiresAt).toLocaleString(locale, {dateStyle: 'short', timeStyle: 'short'})}</strong></span></div>
          </div>
          {invitation.status === 'accepted' ? <div className="booking-invitation-result booking-invitation-result--success"><h2>{t('BOOKING_TOPUP_ACCEPTED_TITLE')}</h2><p>{t('BOOKING_TOPUP_ACCEPTED_BODY')}</p></div> : null}
          {invitation.status === 'rejected' ? <div className="booking-invitation-result"><h2>{t('BOOKING_TOPUP_REJECTED_TITLE')}</h2><p>{t('BOOKING_TOPUP_REJECTED_BODY')}</p></div> : null}
          {invitation.status === 'expired' ? <div className="booking-invitation-result"><h2>{t('BOOKING_TOPUP_EXPIRED_TITLE')}</h2><p>{t('BOOKING_TOPUP_EXPIRED_BODY')}</p></div> : null}
          {invitation.status === 'pending' ? <>
            <div className="booking-invitation-wallet"><FaWallet/><div><small>{t('BOOKING_TOPUP_WALLET')}</small><strong>€ {money(invitation.wallet)}</strong>{Number(invitation.shortfall) > 0 ? <p>{t('BOOKING_TOPUP_SHORTFALL', {amount: money(invitation.shortfall)})}</p> : <p>{t('BOOKING_TOPUP_CREDIT_READY')}</p>}</div></div>
            {error === 'topup_invitation_not_enough_credit' ? <p className="booking-invitation-error">{t('BOOKING_TOPUP_NOT_ENOUGH')}</p> : null}
            <div className="booking-invitation-actions">
              {Number(invitation.shortfall) > 0 ? <Button className="skywork-primary-action" onClick={recharge}><FaWallet/> {t('BOOKING_TOPUP_RECHARGE')}</Button> : <Button className="skywork-primary-action" disabled={processing} onClick={accept}><FaUserCheck/> {t('BOOKING_TOPUP_ACCEPT_PAY', {amount: money(invitation.participantAmount)})}</Button>}
              <Button variant="outline-danger" disabled={processing} onClick={reject}>{t('BOOKING_TOPUP_REJECT')}</Button>
            </div>
            <p className="booking-invitation-note">{t('BOOKING_TOPUP_NOTE')}</p>
          </> : null}
        </section> : null}
      </Col></Container>
    </main>
  );
};

BookingTopupInvitation.propTypes = {
  profile: PropTypes.object,
  authToken: PropTypes.string,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

BookingTopupInvitation.defaultProps = {profile: null, authToken: null};

const mapStateToProps = ({login: {profile, token}}) => ({profile, authToken: token});

export default withRouter(connect(mapStateToProps)(BookingTopupInvitation));
